import type { Row } from "./types.js";

/**
 * 채팅 영역(ChatRoomView / ChatsView / ChatSettingsPanel / MessageContent /
 * PromptPreview) UI 문자열. 키 접두사: chat.* / chats.* / chatSet.* / prompt.*
 *
 * 번역 원칙(strings.ts와 동일): 기계 번역이 아니라 지역 늬앙스를 반영한다. en-US는
 * 항상 채운다(폴백 기준). en-GB는 미국식과 의미 차이가 있을 때만 채운다.
 */
export const chatMessages = {
  // ─── ChatRoomView ────────────────────────────────────────────────────────
  "chat.backToList": {
    ko: "대화 목록으로", "en-US": "Back to chats", ja: "チャット一覧へ", zh: "返回对话列表",
    fr: "Retour aux discussions", de: "Zurück zu den Chats", es: "Volver a los chats",
    it: "Torna alle chat", pt: "Voltar às conversas", "pt-BR": "Voltar às conversas",
    ru: "К списку чатов", ar: "العودة إلى المحادثات", hi: "चैट सूची पर वापस", id: "Kembali ke obrolan",
  },
  "chat.settings": {
    ko: "대화 설정", "en-US": "Chat settings", ja: "チャット設定", zh: "对话设置",
    fr: "Paramètres de la discussion", de: "Chat-Einstellungen", es: "Ajustes del chat",
    it: "Impostazioni della chat", pt: "Definições da conversa", "pt-BR": "Configurações da conversa",
    ru: "Настройки чата", ar: "إعدادات المحادثة", hi: "चैट सेटिंग्स", id: "Pengaturan obrolan",
  },
  "chat.titleFallback": {
    ko: "대화", "en-US": "Chat", ja: "チャット", zh: "对话", fr: "Discussion",
    de: "Chat", es: "Chat", it: "Chat", pt: "Conversa", "pt-BR": "Conversa",
    ru: "Чат", ar: "محادثة", hi: "चैट", id: "Obrolan",
  },
  "chat.usageTitle": {
    ko: "컨텍스트 사용량 추정: {used} / {budget} 토큰{over}",
    "en-US": "Estimated context usage: {used} / {budget} tokens{over}",
    ja: "コンテキスト使用量の推定: {used} / {budget} トークン{over}",
    zh: "上下文用量估算：{used} / {budget} 个 token{over}",
    fr: "Utilisation estimée du contexte : {used} / {budget} jetons{over}",
    de: "Geschätzte Kontextnutzung: {used} / {budget} Tokens{over}",
    es: "Uso estimado del contexto: {used} / {budget} tokens{over}",
    it: "Utilizzo stimato del contesto: {used} / {budget} token{over}",
    pt: "Utilização estimada do contexto: {used} / {budget} tokens{over}",
    "pt-BR": "Uso estimado de contexto: {used} / {budget} tokens{over}",
    ru: "Оценка использования контекста: {used} / {budget} токенов{over}",
    ar: "تقدير استخدام السياق: {used} / {budget} رمز{over}",
    hi: "अनुमानित संदर्भ उपयोग: {used} / {budget} टोकन{over}",
    id: "Perkiraan penggunaan konteks: {used} / {budget} token{over}",
  },
  "chat.usageOver": {
    ko: " (초과 — 오래된 메시지는 트림됩니다)",
    "en-US": " (over — older messages will be trimmed)",
    ja: "（超過 — 古いメッセージは切り詰められます）",
    zh: "（超出 — 较旧的消息将被裁剪）",
    fr: " (dépassé — les anciens messages seront tronqués)",
    de: " (überschritten – ältere Nachrichten werden gekürzt)",
    es: " (excedido — los mensajes antiguos se recortarán)",
    it: " (superato — i messaggi più vecchi verranno tagliati)",
    pt: " (excedido — as mensagens antigas serão cortadas)",
    "pt-BR": " (excedido — as mensagens antigas serão cortadas)",
    ru: " (превышено — старые сообщения будут обрезаны)",
    ar: " (تجاوز — سيتم اقتطاع الرسائل الأقدم)",
    hi: " (सीमा पार — पुराने संदेश हटा दिए जाएँगे)",
    id: " (melebihi — pesan lama akan dipangkas)",
  },
  "chat.previewPromptTitle": {
    ko: "모델에 보내는 프롬프트 미리보기", "en-US": "Preview the prompt sent to the model",
    ja: "モデルに送るプロンプトをプレビュー", zh: "预览发送给模型的提示词",
    fr: "Prévisualiser le prompt envoyé au modèle", de: "An das Modell gesendeten Prompt ansehen",
    es: "Previsualizar el prompt enviado al modelo", it: "Anteprima del prompt inviato al modello",
    pt: "Pré-visualizar o prompt enviado ao modelo", "pt-BR": "Visualizar o prompt enviado ao modelo",
    ru: "Предпросмотр промпта, отправляемого модели", ar: "معاينة الموجّه المُرسَل إلى النموذج",
    hi: "मॉडल को भेजे जाने वाले प्रॉम्प्ट का पूर्वावलोकन", id: "Pratinjau prompt yang dikirim ke model",
  },
  "chat.loadMore": {
    ko: "이전 메시지 {count}개 더보기 (총 {hidden}개 숨김)",
    "en-US": "Show {count} earlier messages ({hidden} hidden in total)",
    ja: "以前のメッセージを {count} 件表示（合計 {hidden} 件非表示）",
    zh: "显示之前的 {count} 条消息（共隐藏 {hidden} 条）",
    fr: "Afficher {count} messages précédents ({hidden} masqués au total)",
    de: "{count} ältere Nachrichten anzeigen (insgesamt {hidden} ausgeblendet)",
    es: "Mostrar {count} mensajes anteriores ({hidden} ocultos en total)",
    it: "Mostra {count} messaggi precedenti ({hidden} nascosti in totale)",
    pt: "Mostrar {count} mensagens anteriores ({hidden} ocultas no total)",
    "pt-BR": "Mostrar {count} mensagens anteriores ({hidden} ocultas no total)",
    ru: "Показать ещё {count} сообщений (всего скрыто {hidden})",
    ar: "عرض {count} رسالة سابقة (إجمالي المخفي {hidden})",
    hi: "{count} पुराने संदेश दिखाएँ (कुल {hidden} छिपे)",
    id: "Tampilkan {count} pesan sebelumnya ({hidden} disembunyikan)",
  },
  "chat.emptyStart": {
    ko: "메시지를 입력해 대화를 시작하세요.",
    "en-US": "Type a message to start the conversation.",
    ja: "メッセージを入力して会話を始めましょう。",
    zh: "输入消息开始对话吧。",
    fr: "Saisissez un message pour démarrer la conversation.",
    de: "Gib eine Nachricht ein, um das Gespräch zu beginnen.",
    es: "Escribe un mensaje para empezar la conversación.",
    it: "Scrivi un messaggio per iniziare la conversazione.",
    pt: "Escreve uma mensagem para iniciar a conversa.",
    "pt-BR": "Digite uma mensagem para iniciar a conversa.",
    ru: "Введите сообщение, чтобы начать разговор.",
    ar: "اكتب رسالة لبدء المحادثة.",
    hi: "बातचीत शुरू करने के लिए कोई संदेश लिखें।",
    id: "Ketik pesan untuk memulai percakapan.",
  },
  "chat.providerNeeded": {
    ko: "대화하려면 AI 프로바이더와 API 키가 필요해요.",
    "en-US": "You need an AI provider and API key to chat.",
    ja: "チャットには AI プロバイダーと API キーが必要です。",
    zh: "聊天需要一个 AI 提供商和 API 密钥。",
    fr: "Vous avez besoin d'un fournisseur d'IA et d'une clé API pour discuter.",
    de: "Zum Chatten brauchst du einen KI-Anbieter und einen API-Schlüssel.",
    es: "Necesitas un proveedor de IA y una clave API para chatear.",
    it: "Per chattare ti servono un provider IA e una chiave API.",
    pt: "Precisas de um fornecedor de IA e de uma chave de API para conversar.",
    "pt-BR": "Você precisa de um provedor de IA e uma chave de API para conversar.",
    ru: "Для общения нужны провайдер ИИ и ключ API.",
    ar: "تحتاج إلى مزوّد ذكاء اصطناعي ومفتاح API لبدء المحادثة.",
    hi: "चैट के लिए आपको एक AI प्रोवाइडर और API कुंजी चाहिए।",
    id: "Anda memerlukan penyedia AI dan kunci API untuk mengobrol.",
  },
  "chat.goToSettings": {
    ko: "설정으로 가기", "en-US": "Go to settings", ja: "設定へ移動", zh: "前往设置",
    fr: "Aller aux paramètres", de: "Zu den Einstellungen", es: "Ir a ajustes",
    it: "Vai alle impostazioni", pt: "Ir para as definições", "pt-BR": "Ir para as configurações",
    ru: "Перейти к настройкам", ar: "الانتقال إلى الإعدادات", hi: "सेटिंग्स पर जाएँ", id: "Ke pengaturan",
  },
  "chat.gameOver": {
    ko: "게임 종료", "en-US": "Game over", ja: "ゲーム終了", zh: "游戏结束",
    fr: "Partie terminée", de: "Spiel beendet", es: "Fin de la partida",
    it: "Partita finita", pt: "Fim de jogo", "pt-BR": "Fim de jogo",
    ru: "Игра окончена", ar: "انتهت اللعبة", hi: "खेल समाप्त", id: "Permainan selesai",
  },
  "chat.gameOverHint": {
    ko: "지난 플레이 기록은 위로 스크롤하면 볼 수 있어요.",
    "en-US": "Scroll up to revisit your playthrough.",
    ja: "上にスクロールすると過去のプレイ記録を見られます。",
    zh: "向上滚动即可回顾本次游玩记录。",
    fr: "Faites défiler vers le haut pour revoir votre partie.",
    de: "Scrolle nach oben, um deinen Spielverlauf nachzulesen.",
    es: "Desplázate hacia arriba para repasar tu partida.",
    it: "Scorri verso l'alto per rivedere la tua partita.",
    pt: "Desliza para cima para rever a tua sessão.",
    "pt-BR": "Role para cima para rever sua jogatina.",
    ru: "Прокрутите вверх, чтобы пересмотреть прохождение.",
    ar: "مرّر لأعلى لمراجعة جولتك.",
    hi: "अपनी पिछली प्लेथ्रू देखने के लिए ऊपर स्क्रॉल करें।",
    id: "Gulir ke atas untuk meninjau permainan Anda.",
  },
  "chat.restart": {
    ko: "다시 시작", "en-US": "Restart", ja: "やり直す", zh: "重新开始",
    fr: "Recommencer", de: "Neu starten", es: "Reiniciar", it: "Ricomincia",
    pt: "Recomeçar", "pt-BR": "Recomeçar", ru: "Начать заново", ar: "إعادة البدء",
    hi: "फिर से शुरू करें", id: "Mulai ulang",
  },
  "chat.regenLast": {
    ko: "마지막 응답 재생성", "en-US": "Regenerate last response", ja: "最後の応答を再生成",
    zh: "重新生成上一条回复", fr: "Régénérer la dernière réponse", de: "Letzte Antwort neu generieren",
    es: "Regenerar la última respuesta", it: "Rigenera l'ultima risposta",
    pt: "Regenerar a última resposta", "pt-BR": "Regenerar a última resposta",
    ru: "Сгенерировать последний ответ заново", ar: "إعادة توليد آخر رد",
    hi: "अंतिम उत्तर फिर से बनाएँ", id: "Buat ulang balasan terakhir",
  },
  "chat.regenTitle": {
    ko: "재생성 (스와이프)", "en-US": "Regenerate (swipe)", ja: "再生成（スワイプ）",
    zh: "重新生成（滑动）", fr: "Régénérer (balayer)", de: "Neu generieren (wischen)",
    es: "Regenerar (deslizar)", it: "Rigenera (scorri)", pt: "Regenerar (deslizar)",
    "pt-BR": "Regenerar (deslizar)", ru: "Сгенерировать заново (свайп)",
    ar: "إعادة التوليد (تمرير)", hi: "फिर से बनाएँ (स्वाइप)", id: "Buat ulang (geser)",
  },
  "chat.inputPlaceholder": {
    ko: "메시지를 입력하세요...", "en-US": "Type a message...", ja: "メッセージを入力...",
    zh: "输入消息...", fr: "Saisissez un message...", de: "Nachricht eingeben...",
    es: "Escribe un mensaje...", it: "Scrivi un messaggio...", pt: "Escreve uma mensagem...",
    "pt-BR": "Digite uma mensagem...", ru: "Введите сообщение...", ar: "اكتب رسالة...",
    hi: "संदेश लिखें...", id: "Ketik pesan...",
  },
  "chat.stop": {
    ko: "중지", "en-US": "Stop", ja: "停止", zh: "停止", fr: "Arrêter",
    de: "Stopp", es: "Detener", it: "Ferma", pt: "Parar", "pt-BR": "Parar",
    ru: "Стоп", ar: "إيقاف", hi: "रोकें", id: "Hentikan",
  },
  "chat.send": {
    ko: "전송", "en-US": "Send", ja: "送信", zh: "发送", fr: "Envoyer",
    de: "Senden", es: "Enviar", it: "Invia", pt: "Enviar", "pt-BR": "Enviar",
    ru: "Отправить", ar: "إرسال", hi: "भेजें", id: "Kirim",
  },
  "chat.voteMessage": {
    ko: "나는 {label}에게 투표한다.", "en-US": "I vote for {label}.",
    ja: "私は {label} に投票します。", zh: "我投票给 {label}。",
    fr: "Je vote pour {label}.", de: "Ich stimme für {label}.",
    es: "Voto por {label}.", it: "Voto per {label}.",
    pt: "Voto em {label}.", "pt-BR": "Voto em {label}.",
    ru: "Я голосую за {label}.", ar: "أصوّت لـ {label}.",
    hi: "मैं {label} को वोट देता हूँ।", id: "Saya memilih {label}.",
  },
  "chat.prevResponse": {
    ko: "이전 응답", "en-US": "Previous response", ja: "前の応答", zh: "上一条回复",
    fr: "Réponse précédente", de: "Vorherige Antwort", es: "Respuesta anterior",
    it: "Risposta precedente", pt: "Resposta anterior", "pt-BR": "Resposta anterior",
    ru: "Предыдущий ответ", ar: "الرد السابق", hi: "पिछला उत्तर", id: "Balasan sebelumnya",
  },
  "chat.nextResponse": {
    ko: "다음 응답", "en-US": "Next response", ja: "次の応答", zh: "下一条回复",
    fr: "Réponse suivante", de: "Nächste Antwort", es: "Respuesta siguiente",
    it: "Risposta successiva", pt: "Resposta seguinte", "pt-BR": "Próxima resposta",
    ru: "Следующий ответ", ar: "الرد التالي", hi: "अगला उत्तर", id: "Balasan berikutnya",
  },
  "chat.deleteVariantTitle": {
    ko: "이 응답(variant) 삭제", "en-US": "Delete this response variant",
    ja: "この応答（バリアント）を削除", zh: "删除此回复（变体）",
    fr: "Supprimer cette variante de réponse", de: "Diese Antwortvariante löschen",
    es: "Eliminar esta variante de respuesta", it: "Elimina questa variante di risposta",
    pt: "Eliminar esta variante de resposta", "pt-BR": "Excluir esta variante de resposta",
    ru: "Удалить этот вариант ответа", ar: "حذف هذه النسخة من الرد",
    hi: "यह उत्तर (वैरिएंट) हटाएँ", id: "Hapus varian balasan ini",
  },
  "chat.deleteVariantBtn": {
    ko: "응답삭제", "en-US": "Delete response", ja: "応答を削除", zh: "删除回复",
    fr: "Supprimer la réponse", de: "Antwort löschen", es: "Eliminar respuesta",
    it: "Elimina risposta", pt: "Eliminar resposta", "pt-BR": "Excluir resposta",
    ru: "Удалить ответ", ar: "حذف الرد", hi: "उत्तर हटाएँ", id: "Hapus balasan",
  },
  "chat.forkTitle": {
    ko: "이 시점까지 새 대화로 분기", "en-US": "Fork into a new chat up to this point",
    ja: "この時点までを新しいチャットに分岐", zh: "从此处分支为新对话",
    fr: "Bifurquer en une nouvelle discussion jusqu'ici", de: "Bis hierher in einen neuen Chat abzweigen",
    es: "Bifurcar en un chat nuevo hasta este punto", it: "Dirama in una nuova chat fino a qui",
    pt: "Ramificar numa nova conversa até aqui", "pt-BR": "Ramificar em uma nova conversa até aqui",
    ru: "Ответвить в новый чат до этого момента", ar: "تفريع إلى محادثة جديدة حتى هذه النقطة",
    hi: "यहाँ तक नई चैट में फ़ोर्क करें", id: "Cabangkan ke obrolan baru hingga titik ini",
  },
  "chat.fork": {
    ko: "분기", "en-US": "Fork", ja: "分岐", zh: "分支", fr: "Bifurquer",
    de: "Abzweigen", es: "Bifurcar", it: "Dirama", pt: "Ramificar", "pt-BR": "Ramificar",
    ru: "Ответвить", ar: "تفريع", hi: "फ़ोर्क", id: "Cabangkan",
  },
  "chat.deleteMessageTitle": {
    ko: "메시지 삭제", "en-US": "Delete message", ja: "メッセージを削除", zh: "删除消息",
    fr: "Supprimer le message", de: "Nachricht löschen", es: "Eliminar mensaje",
    it: "Elimina messaggio", pt: "Eliminar mensagem", "pt-BR": "Excluir mensagem",
    ru: "Удалить сообщение", ar: "حذف الرسالة", hi: "संदेश हटाएँ", id: "Hapus pesan",
  },
  "chat.reasoning": {
    ko: "추론 과정", "en-US": "Reasoning", ja: "推論の過程", zh: "推理过程",
    fr: "Raisonnement", de: "Denkprozess", es: "Razonamiento", it: "Ragionamento",
    pt: "Raciocínio", "pt-BR": "Raciocínio", ru: "Рассуждение", ar: "عملية الاستدلال",
    hi: "तर्क प्रक्रिया", id: "Penalaran",
  },
  "chat.reasoningStreaming": {
    ko: " (생성 중...)", "en-US": " (generating...)", ja: "（生成中...）", zh: "（生成中...）",
    fr: " (génération...)", de: " (wird erzeugt...)", es: " (generando...)",
    it: " (generazione...)", pt: " (a gerar...)", "pt-BR": " (gerando...)",
    ru: " (генерация...)", ar: " (جارٍ التوليد...)", hi: " (बन रहा है...)", id: " (membuat...)",
  },
  "chat.close": {
    ko: "닫기", "en-US": "Close", ja: "閉じる", zh: "关闭", fr: "Fermer",
    de: "Schließen", es: "Cerrar", it: "Chiudi", pt: "Fechar", "pt-BR": "Fechar",
    ru: "Закрыть", ar: "إغلاق", hi: "बंद करें", id: "Tutup",
  },

  // ─── ChatsView ───────────────────────────────────────────────────────────
  "chats.importFailed": {
    ko: "가져오기 실패: {error}", "en-US": "Import failed: {error}",
    ja: "インポートに失敗: {error}", zh: "导入失败：{error}",
    fr: "Échec de l'importation : {error}", de: "Import fehlgeschlagen: {error}",
    es: "Error al importar: {error}", it: "Importazione non riuscita: {error}",
    pt: "Falha na importação: {error}", "pt-BR": "Falha na importação: {error}",
    ru: "Не удалось импортировать: {error}", ar: "فشل الاستيراد: {error}",
    hi: "आयात विफल: {error}", id: "Impor gagal: {error}",
  },
  "chats.count": {
    ko: "{count}개의 대화", "en-US": "{count} chats", ja: "{count} 件のチャット",
    zh: "{count} 个对话", fr: "{count} discussions", de: "{count} Chats",
    es: "{count} chats", it: "{count} chat", pt: "{count} conversas", "pt-BR": "{count} conversas",
    ru: "Чатов: {count}", ar: "{count} محادثة", hi: "{count} चैट", id: "{count} obrolan",
  },
  "chats.importTitle": {
    ko: "대화 번들 JSON 가져오기", "en-US": "Import chat bundle JSON",
    ja: "チャットバンドル JSON をインポート", zh: "导入对话包 JSON",
    fr: "Importer le bundle de discussion (JSON)", de: "Chat-Bundle-JSON importieren",
    es: "Importar el paquete de chat (JSON)", it: "Importa il bundle della chat (JSON)",
    pt: "Importar pacote de conversa (JSON)", "pt-BR": "Importar pacote de conversa (JSON)",
    ru: "Импорт JSON-набора чата", ar: "استيراد حزمة المحادثة بصيغة JSON",
    hi: "चैट बंडल JSON आयात करें", id: "Impor bundel obrolan JSON",
  },
  "chats.newChat": {
    ko: "새 대화", "en-US": "New chat", ja: "新しいチャット", zh: "新对话",
    fr: "Nouvelle discussion", de: "Neuer Chat", es: "Nuevo chat", it: "Nuova chat",
    pt: "Nova conversa", "pt-BR": "Nova conversa", ru: "Новый чат", ar: "محادثة جديدة",
    hi: "नई चैट", id: "Obrolan baru",
  },
  "chats.searchPlaceholder": {
    ko: "대화 제목 검색", "en-US": "Search chat titles", ja: "チャットのタイトルを検索",
    zh: "搜索对话标题", fr: "Rechercher dans les titres", de: "Chat-Titel durchsuchen",
    es: "Buscar títulos de chats", it: "Cerca tra i titoli delle chat",
    pt: "Pesquisar títulos das conversas", "pt-BR": "Pesquisar títulos das conversas",
    ru: "Поиск по названиям чатов", ar: "البحث في عناوين المحادثات",
    hi: "चैट शीर्षक खोजें", id: "Cari judul obrolan",
  },
  "chats.empty": {
    ko: "아직 대화가 없어요. “새 대화”로 시작하거나 캐릭터 화면에서 카드를 선택해 보세요.",
    "en-US": "No chats yet. Start one with “New chat” or pick a card from the Characters screen.",
    ja: "まだチャットがありません。「新しいチャット」から始めるか、キャラクター画面でカードを選んでみましょう。",
    zh: "还没有对话。点击“新对话”开始，或在角色页面选择一张卡片。",
    fr: "Aucune discussion pour l'instant. Commencez avec « Nouvelle discussion » ou choisissez une carte dans l'écran Personnages.",
    de: "Noch keine Chats. Beginne einen mit „Neuer Chat“ oder wähle eine Karte im Charakter-Bildschirm.",
    es: "Aún no hay chats. Empieza uno con «Nuevo chat» o elige una tarjeta en la pantalla de Personajes.",
    it: "Ancora nessuna chat. Iniziane una con «Nuova chat» o scegli una scheda dalla schermata Personaggi.",
    pt: "Ainda não há conversas. Começa uma com «Nova conversa» ou escolhe um cartão no ecrã Personagens.",
    "pt-BR": "Ainda não há conversas. Comece uma com “Nova conversa” ou escolha um card na tela de Personagens.",
    ru: "Чатов пока нет. Начните с «Новый чат» или выберите карточку на экране «Персонажи».",
    ar: "لا توجد محادثات بعد. ابدأ واحدة عبر «محادثة جديدة» أو اختر بطاقة من شاشة الشخصيات.",
    hi: "अभी कोई चैट नहीं है। “नई चैट” से शुरू करें या पात्र स्क्रीन से कोई कार्ड चुनें।",
    id: "Belum ada obrolan. Mulai dengan “Obrolan baru” atau pilih kartu dari layar Karakter.",
  },
  "chats.noResults": {
    ko: "검색 결과가 없어요.", "en-US": "No results.", ja: "検索結果がありません。",
    zh: "没有搜索结果。", fr: "Aucun résultat.", de: "Keine Ergebnisse.",
    es: "Sin resultados.", it: "Nessun risultato.", pt: "Sem resultados.",
    "pt-BR": "Nenhum resultado.", ru: "Ничего не найдено.", ar: "لا توجد نتائج.",
    hi: "कोई परिणाम नहीं।", id: "Tidak ada hasil.",
  },
  "chats.pinned": {
    ko: "고정됨", "en-US": "Pinned", ja: "固定済み", zh: "已置顶",
    fr: "Épinglé", de: "Angeheftet", es: "Fijado", it: "Fissato",
    pt: "Fixado", "pt-BR": "Fixado", ru: "Закреплено", ar: "مثبَّت",
    hi: "पिन किया गया", id: "Disematkan",
  },
  "chats.messageCount": {
    ko: "{count}개 메시지", "en-US": "{count} messages", ja: "{count} 件のメッセージ",
    zh: "{count} 条消息", fr: "{count} messages", de: "{count} Nachrichten",
    es: "{count} mensajes", it: "{count} messaggi", pt: "{count} mensagens",
    "pt-BR": "{count} mensagens", ru: "Сообщений: {count}", ar: "{count} رسالة",
    hi: "{count} संदेश", id: "{count} pesan",
  },
  "chats.unpin": {
    ko: "고정 해제", "en-US": "Unpin", ja: "固定を解除", zh: "取消置顶",
    fr: "Désépingler", de: "Lösen", es: "Dejar de fijar", it: "Rimuovi dai fissati",
    pt: "Desafixar", "pt-BR": "Desafixar", ru: "Открепить", ar: "إلغاء التثبيت",
    hi: "अनपिन करें", id: "Lepas sematan",
  },
  "chats.pin": {
    ko: "고정", "en-US": "Pin", ja: "固定", zh: "置顶", fr: "Épingler",
    de: "Anheften", es: "Fijar", it: "Fissa", pt: "Fixar", "pt-BR": "Fixar",
    ru: "Закрепить", ar: "تثبيت", hi: "पिन करें", id: "Sematkan",
  },
  "chats.archive": {
    ko: "보관", "en-US": "Archive", ja: "アーカイブ", zh: "归档", fr: "Archiver",
    de: "Archivieren", es: "Archivar", it: "Archivia", pt: "Arquivar", "pt-BR": "Arquivar",
    ru: "В архив", ar: "أرشفة", hi: "संग्रहित करें", id: "Arsipkan",
  },
  "chats.deleteConfirm": {
    ko: "\"{title}\" 대화를 삭제할까요? 되돌릴 수 없습니다.",
    "en-US": "Delete the chat \"{title}\"? This can't be undone.",
    ja: "チャット「{title}」を削除しますか？元には戻せません。",
    zh: "删除对话“{title}”？此操作无法撤销。",
    fr: "Supprimer la discussion « {title} » ? Cette action est irréversible.",
    de: "Chat „{title}“ löschen? Das lässt sich nicht rückgängig machen.",
    es: "¿Eliminar el chat «{title}»? No se puede deshacer.",
    it: "Eliminare la chat «{title}»? L'operazione è irreversibile.",
    pt: "Eliminar a conversa \"{title}\"? Não é possível anular.",
    "pt-BR": "Excluir a conversa \"{title}\"? Não dá para desfazer.",
    ru: "Удалить чат «{title}»? Это действие нельзя отменить.",
    ar: "حذف المحادثة \"{title}\"؟ لا يمكن التراجع عن هذا الإجراء.",
    hi: "चैट \"{title}\" हटाएँ? इसे पूर्ववत नहीं किया जा सकता।",
    id: "Hapus obrolan \"{title}\"? Tindakan ini tidak bisa dibatalkan.",
  },
  "chats.whatToPlay": {
    ko: "무엇을 플레이할까요?", "en-US": "What would you like to play?",
    ja: "何をプレイしますか？", zh: "想玩点什么？",
    fr: "À quoi voulez-vous jouer ?", de: "Was möchtest du spielen?",
    es: "¿A qué quieres jugar?", it: "A cosa vuoi giocare?",
    pt: "O que queres jogar?", "pt-BR": "O que você quer jogar?",
    ru: "Во что хотите сыграть?", ar: "بماذا تريد أن تلعب؟",
    hi: "आप क्या खेलना चाहेंगे?", id: "Mau main apa?",
  },
  "chats.custom": {
    ko: "커스텀", "en-US": "Custom", ja: "カスタム", zh: "自定义",
    fr: "Personnalisé", de: "Benutzerdefiniert", es: "Personalizado", it: "Personalizzato",
    pt: "Personalizado", "pt-BR": "Personalizado", ru: "Свой", ar: "مخصّص",
    hi: "कस्टम", id: "Khusus",
  },

  // ─── ChatSettingsPanel ─────────────────────────────────────────────────────
  "chatSet.fieldTitle": {
    ko: "제목", "en-US": "Title", ja: "タイトル", zh: "标题", fr: "Titre",
    de: "Titel", es: "Título", it: "Titolo", pt: "Título", "pt-BR": "Título",
    ru: "Название", ar: "العنوان", hi: "शीर्षक", id: "Judul",
  },
  "chatSet.persona": {
    ko: "페르소나 (@user)", "en-US": "Persona (@user)", ja: "ペルソナ (@user)",
    zh: "角色身份 (@user)", fr: "Persona (@user)", de: "Persona (@user)",
    es: "Persona (@user)", it: "Persona (@user)", pt: "Persona (@user)",
    "pt-BR": "Persona (@user)", ru: "Персона (@user)", ar: "الشخصية (@user)",
    hi: "पर्सोना (@user)", id: "Persona (@user)",
  },
  "chatSet.defaultPersona": {
    ko: "기본 페르소나", "en-US": "Default persona", ja: "デフォルトのペルソナ",
    zh: "默认角色身份", fr: "Persona par défaut", de: "Standard-Persona",
    es: "Persona predeterminada", it: "Persona predefinita", pt: "Persona predefinida",
    "pt-BR": "Persona padrão", ru: "Персона по умолчанию", ar: "الشخصية الافتراضية",
    hi: "डिफ़ॉल्ट पर्सोना", id: "Persona bawaan",
  },
  "chatSet.preset": {
    ko: "프리셋", "en-US": "Preset", ja: "プリセット", zh: "预设", fr: "Préréglage",
    de: "Preset", es: "Preajuste", it: "Preset", pt: "Predefinição", "pt-BR": "Predefinição",
    ru: "Пресет", ar: "إعداد مسبق", hi: "प्रीसेट", id: "Praatur",
  },
  "chatSet.emptyGlobalHint": {
    ko: "비우면 전역 기본 사용", "en-US": "Leave empty to use the global default",
    ja: "空欄なら全体のデフォルトを使用", zh: "留空则使用全局默认",
    fr: "Laissez vide pour utiliser la valeur globale par défaut",
    de: "Leer lassen, um den globalen Standard zu verwenden",
    es: "Déjalo vacío para usar el valor global predeterminado",
    it: "Lascia vuoto per usare il valore globale predefinito",
    pt: "Deixa vazio para usar a predefinição global",
    "pt-BR": "Deixe vazio para usar o padrão global",
    ru: "Оставьте пустым для глобального значения по умолчанию",
    ar: "اتركه فارغًا لاستخدام الإعداد الافتراضي العام",
    hi: "वैश्विक डिफ़ॉल्ट उपयोग हेतु खाली छोड़ें",
    id: "Kosongkan untuk memakai bawaan global",
  },
  "chatSet.globalDefault": {
    ko: "전역 기본", "en-US": "Global default", ja: "全体のデフォルト", zh: "全局默认",
    fr: "Valeur globale par défaut", de: "Globaler Standard", es: "Predeterminado global",
    it: "Predefinito globale", pt: "Predefinição global", "pt-BR": "Padrão global",
    ru: "Глобальное по умолчанию", ar: "الافتراضي العام", hi: "वैश्विक डिफ़ॉल्ट", id: "Bawaan global",
  },
  "chatSet.provider": {
    ko: "프로바이더", "en-US": "Provider", ja: "プロバイダー", zh: "提供商",
    fr: "Fournisseur", de: "Anbieter", es: "Proveedor", it: "Provider",
    pt: "Fornecedor", "pt-BR": "Provedor", ru: "Провайдер", ar: "المزوّد",
    hi: "प्रोवाइडर", id: "Penyedia",
  },
  "chatSet.authorNote": {
    ko: "작가 노트 (author's note)", "en-US": "Author's note", ja: "作者ノート（author's note）",
    zh: "作者注释（author's note）", fr: "Note de l'auteur (author's note)",
    de: "Autorennotiz (author's note)", es: "Nota del autor (author's note)",
    it: "Nota dell'autore (author's note)", pt: "Nota do autor (author's note)",
    "pt-BR": "Nota do autor (author's note)", ru: "Заметка автора (author's note)",
    ar: "ملاحظة المؤلّف (author's note)", hi: "लेखक नोट (author's note)",
    id: "Catatan penulis (author's note)",
  },
  "chatSet.authorNoteHint": {
    ko: "대화 맥락에 주입되는 지시", "en-US": "Instructions injected into the chat context",
    ja: "会話の文脈に注入される指示", zh: "注入到对话上下文中的指令",
    fr: "Instructions injectées dans le contexte de la discussion",
    de: "Anweisungen, die in den Chat-Kontext eingefügt werden",
    es: "Instrucciones inyectadas en el contexto del chat",
    it: "Istruzioni inserite nel contesto della chat",
    pt: "Instruções injetadas no contexto da conversa",
    "pt-BR": "Instruções injetadas no contexto da conversa",
    ru: "Инструкции, вставляемые в контекст чата",
    ar: "تعليمات تُحقَن في سياق المحادثة",
    hi: "चैट संदर्भ में जोड़े जाने वाले निर्देश",
    id: "Instruksi yang disisipkan ke konteks obrolan",
  },
  "chatSet.authorNoteDepth": {
    ko: "작가 노트 깊이: {depth}", "en-US": "Author's note depth: {depth}",
    ja: "作者ノートの深さ: {depth}", zh: "作者注释深度：{depth}",
    fr: "Profondeur de la note de l'auteur : {depth}", de: "Tiefe der Autorennotiz: {depth}",
    es: "Profundidad de la nota del autor: {depth}", it: "Profondità della nota dell'autore: {depth}",
    pt: "Profundidade da nota do autor: {depth}", "pt-BR": "Profundidade da nota do autor: {depth}",
    ru: "Глубина заметки автора: {depth}", ar: "عمق ملاحظة المؤلّف: {depth}",
    hi: "लेखक नोट गहराई: {depth}", id: "Kedalaman catatan penulis: {depth}",
  },
  "chatSet.authorNoteDepthHint": {
    ko: "최근 몇 번째 메시지 앞에 주입할지", "en-US": "How many messages back to inject it",
    ja: "直近の何件目のメッセージの前に注入するか", zh: "在最近第几条消息前注入",
    fr: "Combien de messages en arrière l'injecter", de: "Wie viele Nachrichten zurück sie eingefügt wird",
    es: "Cuántos mensajes atrás inyectarla", it: "Quanti messaggi indietro inserirla",
    pt: "Quantas mensagens atrás injetá-la", "pt-BR": "Quantas mensagens atrás injetá-la",
    ru: "За сколько сообщений до конца вставлять", ar: "قبل كم رسالة من الأحدث يتم حقنها",
    hi: "हाल के कितने संदेश पहले इसे जोड़ें", id: "Berapa pesan ke belakang untuk menyisipkannya",
  },
  "chatSet.chatLorebooks": {
    ko: "채팅 로어북", "en-US": "Chat lorebooks", ja: "チャットのロアブック",
    zh: "对话设定集", fr: "Codex de la discussion", de: "Chat-Lorebooks",
    es: "Compendios del chat", it: "Codex della chat", pt: "Compêndios da conversa",
    "pt-BR": "Compêndios da conversa", ru: "Лорбуки чата", ar: "كتب معرفة المحادثة",
    hi: "चैट लोरबुक", id: "Lorebook obrolan",
  },
  "chatSet.chatLorebooksHint": {
    ko: "캐릭터 임베드에 더해 적용", "en-US": "Applied on top of character embeds",
    ja: "キャラクター埋め込みに加えて適用", zh: "在角色内嵌之外额外应用",
    fr: "Appliqué en plus des codex intégrés au personnage",
    de: "Zusätzlich zu den im Charakter eingebetteten angewendet",
    es: "Se aplica además de los integrados en el personaje",
    it: "Applicato in aggiunta a quelli incorporati nel personaggio",
    pt: "Aplicado além dos incorporados na personagem",
    "pt-BR": "Aplicado além dos incorporados no personagem",
    ru: "Применяется поверх встроенных в персонажа",
    ar: "يُطبَّق إضافةً إلى المضمَّنة في الشخصية",
    hi: "पात्र में एम्बेड किए गए के अतिरिक्त लागू",
    id: "Diterapkan selain yang tertanam di karakter",
  },
  "chatSet.noLorebooks": {
    ko: "로어북이 없습니다. 로어북 화면에서 만드세요.",
    "en-US": "No lorebooks. Create one in the Lorebooks screen.",
    ja: "ロアブックがありません。ロアブック画面で作成してください。",
    zh: "没有设定集。请在设定集页面创建。",
    fr: "Aucun codex. Créez-en un dans l'écran Codex.",
    de: "Keine Lorebooks. Erstelle eins im Lorebook-Bildschirm.",
    es: "No hay compendios. Crea uno en la pantalla de Compendios.",
    it: "Nessun codex. Creane uno nella schermata Codex.",
    pt: "Sem compêndios. Cria um no ecrã Compêndios.",
    "pt-BR": "Sem compêndios. Crie um na tela de Compêndios.",
    ru: "Лорбуков нет. Создайте один на экране «Лорбуки».",
    ar: "لا توجد كتب معرفة. أنشئ واحدًا من شاشة كتب المعرفة.",
    hi: "कोई लोरबुक नहीं। लोरबुक स्क्रीन में बनाएँ।",
    id: "Belum ada lorebook. Buat satu di layar Lorebook.",
  },

  // ─── PromptPreview ─────────────────────────────────────────────────────────
  "prompt.title": {
    ko: "프롬프트 미리보기", "en-US": "Prompt preview", ja: "プロンプトのプレビュー",
    zh: "提示词预览", fr: "Aperçu du prompt", de: "Prompt-Vorschau",
    es: "Vista previa del prompt", it: "Anteprima del prompt", pt: "Pré-visualização do prompt",
    "pt-BR": "Pré-visualização do prompt", ru: "Предпросмотр промпта", ar: "معاينة الموجّه",
    hi: "प्रॉम्प्ट पूर्वावलोकन", id: "Pratinjau prompt",
  },
  "prompt.assembling": {
    ko: "조립 중...", "en-US": "Assembling...", ja: "組み立て中...", zh: "正在组装...",
    fr: "Assemblage...", de: "Wird zusammengestellt...", es: "Ensamblando...",
    it: "Assemblaggio...", pt: "A montar...", "pt-BR": "Montando...",
    ru: "Сборка...", ar: "جارٍ التجميع...", hi: "जोड़ा जा रहा है...", id: "Menyusun...",
  },
  "prompt.usage": {
    ko: "추정 {used} / {budget} 토큰 ({pct}%{over})",
    "en-US": "Estimated {used} / {budget} tokens ({pct}%{over})",
    ja: "推定 {used} / {budget} トークン（{pct}%{over}）",
    zh: "估算 {used} / {budget} 个 token（{pct}%{over}）",
    fr: "Estimé {used} / {budget} jetons ({pct}%{over})",
    de: "Geschätzt {used} / {budget} Tokens ({pct}%{over})",
    es: "Estimado {used} / {budget} tokens ({pct}%{over})",
    it: "Stimato {used} / {budget} token ({pct}%{over})",
    pt: "Estimado {used} / {budget} tokens ({pct}%{over})",
    "pt-BR": "Estimado {used} / {budget} tokens ({pct}%{over})",
    ru: "Оценка {used} / {budget} токенов ({pct}%{over})",
    ar: "تقديري {used} / {budget} رمز ({pct}%{over})",
    hi: "अनुमानित {used} / {budget} टोकन ({pct}%{over})",
    id: "Perkiraan {used} / {budget} token ({pct}%{over})",
  },
  "prompt.usageOver": {
    ko: ", 초과 — 오래된 메시지 트림됨", "en-US": ", over — older messages trimmed",
    ja: "、超過 — 古いメッセージは切り詰め", zh: "，超出 — 较旧消息已裁剪",
    fr: ", dépassé — anciens messages tronqués", de: ", überschritten – ältere Nachrichten gekürzt",
    es: ", excedido — mensajes antiguos recortados", it: ", superato — messaggi vecchi tagliati",
    pt: ", excedido — mensagens antigas cortadas", "pt-BR": ", excedido — mensagens antigas cortadas",
    ru: ", превышено — старые сообщения обрезаны", ar: "، تجاوز — تم اقتطاع الرسائل الأقدم",
    hi: ", सीमा पार — पुराने संदेश हटाए गए", id: ", melebihi — pesan lama dipangkas",
  },
} satisfies Record<string, Row>;
